import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { Component, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import * as XLSX from 'xlsx';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { CommonModule, CurrencyPipe } from '@angular/common';

import { SessionStorageService } from "../../../services/session-storage.service";
import { AccountingReportsService, ReportDiagnostic } from "../../accounting-reports.service";
import { ChartOfAccountsService } from "../../../chart-of-accounts/chart-of-accounts.service";
import { ChartOfAccounts } from "../../../chart-of-accounts/chart-of-accounts.model";
import { BalanceSheet as BalanceSheetModel } from "./balance-sheet.model";
import { BalanceSheetItem } from "./balance-sheet-item.model"; // Alias to avoid conflict
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { ToastModule } from 'primeng/toast';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { ProgressBarModule } from "primeng/progressbar";
import { DividerModule } from "primeng/divider";
import { RippleModule } from 'primeng/ripple';
import { IftaLabelModule } from 'primeng/iftalabel';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { ReportEmailComponent } from '../shared/report-email.component';
import { buildAccountingReportPeriodLabels } from '../shared/accounting-report-periods';
import { AccountingReportTreeCellComponent } from '../shared/accounting-report-tree-cell.component';
import { closeFinancialReportPdf, downloadFinancialReportPdf, openFinancialReportPdf } from '../shared/financial-report-pdf';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';

@Component({
    selector: 'app-balance-sheet',
    templateUrl: './balance-sheet.component.html',
    styleUrls: [
        './balance-sheet.component.css',
        '../shared/accounting-report-tree-table.css'
    ],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        BreadcrumbModule,
        ButtonModule,
        DatePickerModule,
        SelectModule,
        InputNumberModule,
        InputTextModule,
        TableModule,
        RippleModule,
        ToastModule,
        CardModule,
        PanelModule,
        CurrencyPipe,
        ProgressBarModule,
        DividerModule,
        IftaLabelModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        PageDataPanelComponent,
        ReportEmailComponent,
        AccountingReportTreeCellComponent
    ],
    providers: [
        MessageService,
        AccountingReportsService,
        ChartOfAccountsService,
        CurrencyPipe,
        SessionStorageService,
        CurrencyPipe
    ]

})
export class BalanceSheetComponent implements OnInit {
    readonly reportActions = new PendingActionTracker<string>();

    isLoading: boolean = false;
    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;

    // For input fields
    startDate: Date = new Date(new Date().getFullYear(), 0, 1); // Default to start of current year
    endDate: Date = new Date(); // Default to today

    // Summary totals for cards
    totalAssets: number = 0;
    totalLiabilities: number = 0;
    totalEquity: number = 0;
    reportCurrencyCode: string = 'SAR'; // Default currency

    private hierarchicalBalanceSheetItems: BalanceSheetItem[] = []; // Master hierarchical data
    first: number = 0; // For pagination

    // Data for individual tables
    assetItems: BalanceSheetItem[] = [];
    liabilityItems: BalanceSheetItem[] = [];
    equityItems: BalanceSheetItem[] = [];

    totalAssetRecords = 0;
    totalLiabilityRecords = 0;
    totalEquityRecords = 0;

    selectedPeriodType: string = 'yearly'; // Default period type
    periodOptions: any[] = [
        { name: 'Yearly', value: 'yearly' },
        { name: 'Quarterly', value: 'quarterly' },
        { name: 'Monthly', value: 'monthly' }
    ];
    selectedPeriods: string[] = []; // e.g., ['2024', '2025'] or ['Q1 2025', 'Q2 2025']

    // For Balance Sheet Equation Check table
    balanceCheckItems: { metric: string; values: { [period: string]: number } }[] = [];
    diagnostics: ReportDiagnostic[] = [];

    relevantCoaRoots: ChartOfAccounts[] = [];

    selectedLogisticsProjectId?: number | null;

    constructor(
        private accountReportService: AccountingReportsService,
        private chartOfAccountsService: ChartOfAccountsService,
        private messageService: MessageService,
        private currencyPipe: CurrencyPipe,
        private sessionStorageService: SessionStorageService
    ) { }

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Accounts' }, { label: 'Reports' }, { label: 'Balance Sheet' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.updateSelectedPeriods(); // Initialize periods based on default type

        this.loadBalanceSheetData({}); // Initial data load
    }

    onPeriodTypeChange(): void {
        this.updateSelectedPeriods();
        this.loadBalanceSheetData({});
    }

    updateSelectedPeriods(): void {
        this.selectedPeriods = buildAccountingReportPeriodLabels(
            this.startDate,
            this.endDate,
            this.selectedPeriodType
        );
    }

    loadBalanceSheetData(event: TableLazyLoadEvent) {
        if (!this.startDate || !this.endDate) {
            this.messageService.add({ severity: 'warn', summary: 'Missing Dates', detail: 'Please select both start and end dates.' });
            return;
        }

        this.updateSelectedPeriods();

        this.isLoading = true;
        this.first = event.first || 0;

        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company information is missing.' });
            this.isLoading = false;
            return;
        }

        // Use forkJoin to fetch Balance Sheet data and the COA tree in parallel
        forkJoin({
            bsData: this.accountReportService.getBalanceSheetV2(
                companyId,
                this.selectedPeriodType,
                this.startDate.getTime(),
                this.endDate.getTime(),
                this.selectedLogisticsProjectId
            ),
            coaTree: this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, true)
        }).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: ({ bsData, coaTree }) => {
                const reportRows = bsData.rows ?? [];
                this.diagnostics = bsData.diagnostics ?? [];
                if (reportRows.length > 0) {
                    this.reportCurrencyCode = reportRows[0].currencyCode || this.reportCurrencyCode;
                }
                // Filter the COA to only include the roots relevant for a Balance Sheet (Asset, Liability, Equity)
                this.relevantCoaRoots = coaTree.filter(root =>
                    root.accountType?.accountCategory === 'Asset' ||
                    root.accountType?.accountCategory === 'Assets' ||
                    root.accountType?.accountCategory === 'Liability' ||
                    root.accountType?.accountCategory === 'Liabilities' ||
                    root.accountType?.accountCategory === 'Equity'
                );

                // Transform the flat data into a hierarchical structure using only the relevant parts of the COA.
                this.hierarchicalBalanceSheetItems = this.transformToTree(reportRows, this.relevantCoaRoots);

                this.splitAndFlattenData(); // Split data into asset, liability, equity arrays
                this.calculateSummaryTotals(); // Calculate grand totals for summary cards
                this.updateBalanceCheck(); // Update the balance sheet equation check table

                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Balance Sheet data loaded.' });
            },
            error: (err) => {
                console.error('Failed to load Balance Sheet data:', err);
                this.assetItems = [];
                this.liabilityItems = [];
                this.equityItems = [];
                this.hierarchicalBalanceSheetItems = [];
                this.totalAssetRecords = 0;
                this.totalLiabilityRecords = 0;
                this.totalEquityRecords = 0;
                this.totalAssets = 0;
                this.totalLiabilities = 0;
                this.totalEquity = 0;
                this.balanceCheckItems = [];
                this.diagnostics = [];
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load report data. Please try again.' });
            }
        });
    }

    /**
     * Transforms flat BalanceSheetModel data into a hierarchical BalanceSheetItem structure
     * based on the Chart of Accounts tree.
     * @param bsData Flat list of BalanceSheetModel from the API.
     * @param coaTree Hierarchical Chart of Accounts.
     * @returns Hierarchical BalanceSheetItem array.
     */
    private transformToTree(bsData: BalanceSheetModel[], coaTree: ChartOfAccounts[]): BalanceSheetItem[] {
        // Step 1: Create a flat lookup map from accountCode to the full ChartOfAccounts object.
        const coaCodeMap = new Map<string, ChartOfAccounts>();
        const flattenCoaForLookup = (nodes: ChartOfAccounts[]) => {
            for (const node of nodes) {
                coaCodeMap.set(node.accountCode, node);
                if (node.childrenAccounts?.length) {
                    flattenCoaForLookup(node.childrenAccounts);
                }
            }
        };
        flattenCoaForLookup(coaTree);

        // Step 2: Aggregate the flat Balance Sheet data by accountId and PeriodLabel.
        const aggregatedDataMap = new Map<number, Partial<BalanceSheetItem>>();

        for (const item of bsData) {
            if (item.accountCode === 'NET-INCOME') {
                continue;
            }
            const coaNode = coaCodeMap.get(item.accountCode);
            if (!coaNode) {
                console.warn(`Balance Sheet item with code ${item.accountCode} not found in Chart of Accounts.`, item);
                continue;
            }

            const accountId = coaNode.accountId;

            if (!aggregatedDataMap.has(accountId)) {
                aggregatedDataMap.set(accountId, {
                    accountId: accountId,
                    accountCode: item.accountCode,
                    accountName: item.accountName,
                    internalType: item.internalType,
                    currencyCode: item.currencyCode,
                    balances: {}
                });
            }
            const entry = aggregatedDataMap.get(accountId)!;
            // Balance Sheet balances are cumulative up to the end of the period
            entry.balances![item.periodLabel] = (entry.balances![item.periodLabel] || 0) + item.balance;
        }

        // Step 3: Recursively build the tree.
        const buildNode = (coaNode: ChartOfAccounts, level: number): BalanceSheetItem | null => {
            const aggregatedItemData = aggregatedDataMap.get(coaNode.accountId);

            // Only include nodes that are present in the aggregated data OR have children that are.
            if (!aggregatedItemData && (!coaNode.childrenAccounts || coaNode.childrenAccounts.length === 0)) {
                return null;
            }

            const treeNode: BalanceSheetItem = {
                accountId: coaNode.accountId,
                accountCode: coaNode.accountCode,
                accountName: coaNode.accountName,
                level: level,
                isSummaryRow: coaNode.childrenAccounts && coaNode.childrenAccounts.length > 0,
                expanded: level < 1, // Expand top-level categories by default
                children: [],
                internalType: this.normalizeBalanceSheetType(
                    aggregatedItemData?.internalType || coaNode.accountType?.accountCategory
                ),
                currencyCode: aggregatedItemData?.currencyCode || this.reportCurrencyCode,
                balances: aggregatedItemData?.balances || {},
            };

            if (coaNode.childrenAccounts?.length) {
                treeNode.children = coaNode.childrenAccounts
                    .map(childCoa => buildNode(childCoa, level + 1))
                    .filter((childNode): childNode is BalanceSheetItem => childNode !== null);
            }

            // If a summary account has its own balance, expose it as a child so the
            // displayed roll-up can be reconciled from the rows underneath it.
            if (treeNode.isSummaryRow || (treeNode.children && treeNode.children.length > 0)) {
                const ownBalances = { ...treeNode.balances };
                const hasDirectBalance = Object.values(ownBalances).some(value => value !== 0);
                if (hasDirectBalance) {
                    const directBalanceRow: BalanceSheetItem = {
                        accountId: -coaNode.accountId,
                        accountCode: '',
                        accountName: `Direct balance in ${coaNode.accountName}`,
                        level: level + 1,
                        isSummaryRow: false,
                        isDirectBalanceRow: true,
                        expanded: false,
                        children: [],
                        internalType: treeNode.internalType,
                        currencyCode: treeNode.currencyCode,
                        balances: ownBalances
                    };
                    treeNode.children = [directBalanceRow, ...(treeNode.children ?? [])];
                }

                this.selectedPeriods.forEach(period => {
                    treeNode.balances[period] = treeNode.children?.reduce(
                        (sum, child) => sum + (child.balances[period] || 0),
                        0
                    ) ?? 0;
                });
            }

            // Ensure the top-level categories (Asset, Liability, Equity) are always included
            // even if they have no direct transactions, as long as they have children or are in COA.
            if (level === 0 && (treeNode.internalType === 'asset' || treeNode.internalType === 'liability' || treeNode.internalType === 'equity')) {
                return treeNode;
            }

            // Only return nodes that have actual balances or children with balances
            if (Object.keys(treeNode.balances).length > 0 || (treeNode.children && treeNode.children.length > 0)) {
                return treeNode;
            }

            return null;
        };

        // Build the tree starting from the relevant COA roots
        const tree = this.relevantCoaRoots
            .map(rootCoa => buildNode(rootCoa, 0))
            .filter((node): node is BalanceSheetItem => node !== null)
            .sort((a, b) => { // Ensure consistent order of categories (Assets, Liabilities, Equity)
                const order = { 'asset': 1, 'liability': 2, 'equity': 3, 'Uncategorized': 99 };
                return (order[a.internalType as keyof typeof order] || 99) - (order[b.internalType as keyof typeof order] || 99);
            });

        const syntheticPeriods = bsData.filter(item => item.accountCode === 'NET-INCOME');
        const syntheticItems = syntheticPeriods.length > 0 ? [{
            accountId: syntheticPeriods[0].accountId,
            accountCode: syntheticPeriods[0].accountCode,
            accountName: syntheticPeriods[0].accountName,
            level: 0,
            isSummaryRow: false,
            expanded: false,
            children: [],
            internalType: 'equity',
            currencyCode: syntheticPeriods[0].currencyCode || this.reportCurrencyCode,
            balances: syntheticPeriods.reduce((balances, item) => {
                balances[item.periodLabel] = item.balance;
                return balances;
            }, {} as { [period: string]: number })
        } as BalanceSheetItem] : [];

        return [...tree, ...syntheticItems];
    }

    private normalizeBalanceSheetType(value?: string | null): string {
        const normalized = (value || '').toLowerCase();
        if (normalized.includes('asset') || normalized.includes('receiv')) {
            return 'asset';
        }
        if (normalized.includes('liabilit') || normalized.includes('payable')) {
            return 'liability';
        }
        if (normalized.includes('equity')) {
            return 'equity';
        }
        return 'Uncategorized';
    }

    /**
     * Splits the hierarchical data into separate arrays for Assets, Liabilities, and Equity
     * and flattens them for PrimeNG table display.
     */
    private splitAndFlattenData(): void {
        this.assetItems = [];
        this.liabilityItems = [];
        this.equityItems = [];

        const flatten = (items: BalanceSheetItem[], targetArray: BalanceSheetItem[]) => {
            items.forEach(item => {
                targetArray.push(item);
                if (item.children && item.expanded) {
                    flatten(item.children, targetArray);
                }
            });
        };

        this.hierarchicalBalanceSheetItems.forEach(rootItem => {
            if (rootItem.internalType === 'asset') {
                flatten([rootItem], this.assetItems);
            } else if (rootItem.internalType === 'liability') {
                flatten([rootItem], this.liabilityItems);
            } else if (rootItem.internalType === 'equity') {
                flatten([rootItem], this.equityItems);
            }
        });

        this.totalAssetRecords = this.assetItems.length;
        this.totalLiabilityRecords = this.liabilityItems.length;
        this.totalEquityRecords = this.equityItems.length;
    }

    /**
     * Calculates the summary totals for the cards (Total Assets, Total Liabilities, Total Equity)
     * based on the latest period's data.
     */
    calculateSummaryTotals(): void {
        this.totalAssets = 0;
        this.totalLiabilities = 0;
        this.totalEquity = 0;

        // Use the last period in selectedPeriods for the overall summary cards
        const lastPeriod = this.selectedPeriods[this.selectedPeriods.length - 1];

        this.hierarchicalBalanceSheetItems.forEach(rootItem => {
            const balanceForLastPeriod = rootItem.balances[lastPeriod] || 0;

            if (rootItem.internalType === 'asset') {
                this.totalAssets += balanceForLastPeriod;
            } else if (rootItem.internalType === 'liability') {
                this.totalLiabilities += balanceForLastPeriod;
            } else if (rootItem.internalType === 'equity') {
                this.totalEquity += balanceForLastPeriod;
            }
        });
    }

    /**
     * Calculates the total for a specific category (e.g., 'asset', 'liability') for a given period.
     * This is used for the table footers.
     * @param category The internal type of the account ('asset', 'liability', 'equity').
     * @param period The period label (e.g., '2024', 'Q1 2025').
     * @returns The total balance for that category and period.
     */
    getTotalForCategory(category: string, period: string): number {
        let total = 0;
        this.hierarchicalBalanceSheetItems.forEach(rootItem => {
            if (rootItem.internalType === category) {
                total += rootItem.balances[period] || 0;
            }
        });
        return total;
    }

    /**
     * Updates the data for the Balance Sheet Equation Check table.
     */
    updateBalanceCheck(): void {
        const assetsByPeriod: { [period: string]: number } = {};
        const liabilitiesByPeriod: { [period: string]: number } = {};
        const equityByPeriod: { [period: string]: number } = {};
        const checkByPeriod: { [period: string]: number } = {};

        this.selectedPeriods.forEach(period => {
            assetsByPeriod[period] = this.getTotalForCategory('asset', period);
            liabilitiesByPeriod[period] = this.getTotalForCategory('liability', period);
            equityByPeriod[period] = this.getTotalForCategory('equity', period);
            checkByPeriod[period] = assetsByPeriod[period] - (liabilitiesByPeriod[period] + equityByPeriod[period]);
        });

        this.balanceCheckItems = [
            { metric: 'Total Assets', values: assetsByPeriod },
            { metric: 'Total Liabilities', values: liabilitiesByPeriod },
            { metric: 'Total Equity', values: equityByPeriod },
            { metric: 'Assets - (Liabilities + Equity)', values: checkByPeriod }
        ];
    }

    get latestPeriod(): string | null {
        return this.selectedPeriods.length ? this.selectedPeriods[this.selectedPeriods.length - 1] : null;
    }

    get balancedPeriodCount(): number {
        return this.selectedPeriods.filter(period => this.isPeriodBalanced(period)).length;
    }

    get allPeriodsBalanced(): boolean {
        return this.selectedPeriods.length > 0 && this.balancedPeriodCount === this.selectedPeriods.length;
    }

    getEquationDifference(period: string): number {
        return this.getTotalForCategory('asset', period)
            - (this.getTotalForCategory('liability', period) + this.getTotalForCategory('equity', period));
    }

    isPeriodBalanced(period: string): boolean {
        return Math.abs(this.getEquationDifference(period)) < 0.005;
    }

    toggleNode(item: BalanceSheetItem): void {
        item.expanded = !item.expanded;
        // Re-split and flatten data to update table display based on expanded state
        this.splitAndFlattenData();
    }

    getBalanceSide(value: number | null | undefined, internalType: string): 'DR' | 'CR' | 'ZERO' {
        const amount = Number(value ?? 0);
        if (Math.abs(amount) < 0.005) {
            return 'ZERO';
        }

        const normalSide = internalType.toLowerCase() === 'asset' ? 'DR' : 'CR';
        if (amount > 0) {
            return normalSide;
        }
        return normalSide === 'DR' ? 'CR' : 'DR';
    }

    exportExcel(): void {
        if (this.hierarchicalBalanceSheetItems.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'No data to export.' });
            return;
        }

        const workbook = XLSX.utils.book_new();
        const dataToExport: any[][] = [];

        // 1. Add the high-level summary at the top, just like the UI
        dataToExport.push(['Balance Sheet Summary']);
        dataToExport.push(['Total Assets', this.totalAssets]);
        dataToExport.push(['Total Liabilities', this.totalLiabilities]);
        dataToExport.push(['Total Equity', this.totalEquity]);
        dataToExport.push([]); // Add a blank row for spacing

        // Define the main header row for the tables
        const tableHeader = ['Account Code', 'Account Name', ...this.selectedPeriods];

        // Reusable helper function to process a section (e.g., Assets, Liabilities, Equity)
        const processSection = (title: string, items: BalanceSheetItem[], categoryType: 'asset' | 'liability' | 'equity') => {
            if (items.length === 0) {
                return; // Don't create a section if there's no data for it
            }

            dataToExport.push([title]); // Section Title (e.g., "Assets")
            dataToExport.push(tableHeader); // Add the column headers

            // Recursive function to flatten the tree data into rows
            const flattenForExport = (nodes: BalanceSheetItem[]) => {
                nodes.forEach(node => {
                    const row: any[] = [
                        node.accountCode,
                        '  '.repeat(node.level) + node.accountName // Indent account name based on level
                    ];
                    // Add the balance for each period, or 0 if it doesn't exist
                    this.selectedPeriods.forEach(period => row.push(node.balances[period] || 0));
                    dataToExport.push(row);

                    // If the node has children, process them recursively
                    if (node.children) {
                        flattenForExport(node.children);
                    }
                });
            };

            // Flatten the relevant section's data (only top-level items for flattening)
            const topLevelSectionItems = this.hierarchicalBalanceSheetItems.filter(item => item.internalType === categoryType);
            flattenForExport(topLevelSectionItems);

            // Add the total row for the section
            const totalRow: any[] = ['', `Total ${title}`]; // Label for the total row
            this.selectedPeriods.forEach(period => {
                // Use the existing function to get the total for the category and period
                totalRow.push(this.getTotalForCategory(categoryType, period));
            });
            dataToExport.push(totalRow);
            dataToExport.push([]); // Add a blank row for spacing after the section
        };

        // 2. Process and add the Assets section
        processSection('Assets', this.assetItems, 'asset');

        // 3. Process and add the Liabilities section
        processSection('Liabilities', this.liabilityItems, 'liability');

        // 4. Process and add the Equity section
        processSection('Equity', this.equityItems, 'equity');

        // 5. Add Balance Sheet Equation Check table
        dataToExport.push(['Balance Sheet Equation Check']);
        dataToExport.push(['Metric', ...this.selectedPeriods]);
        this.balanceCheckItems.forEach(item => {
            const row: any[] = [item.metric];
            this.selectedPeriods.forEach(period => row.push(item.values[period] || 0));
            dataToExport.push(row);
        });
        dataToExport.push([]); // Add a blank row at the end

        // 6. Generate the Excel file from the prepared data array
        const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(dataToExport);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Balance Sheet');

        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        this.saveAsExcelFile(excelBuffer, "BalanceSheetReport");
        this.messageService.add({ severity: 'success', summary: 'Exported', detail: 'Report exported to Excel.' });
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        const downloadLink: HTMLAnchorElement = document.createElement('a');
        const url = URL.createObjectURL(data);
        downloadLink.href = url;
        downloadLink.download = fileName + '_' + new Date().toISOString().slice(0, 10) + '.xlsx';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
    }

    printReport(download = false): void {
        const action = download ? 'download' : 'print';
        if (!this.reportActions.begin(action)) return;
        const target = download ? null : window.open('', '_blank');
        this.accountReportService.printFinanceReport('balance-sheet', this.sessionStorageService.getCompany()?.id ?? 0, {
            startDate: this.startDate,
            endDate: this.endDate,
            selectedPeriodType: this.selectedPeriodType,
            logisticsProjectId: this.selectedLogisticsProjectId
        }).pipe(finalize(() => this.reportActions.end(action))).subscribe({
            next: blob => download ? downloadFinancialReportPdf(blob, 'balance-sheet') : openFinancialReportPdf(blob, target),
            error: () => {
                closeFinancialReportPdf(target);
                this.messageService.add({ severity: 'error', summary: 'Print unavailable', detail: 'Failed to generate the Balance Sheet PDF.' });
            }
        });
    }

    private legacyPrintReport(): void {
        if (this.hierarchicalBalanceSheetItems.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no data to print.' });
            return;
        }

        // 1. Gather all necessary information
        const company = this.sessionStorageService.getCompany();
        const user = this.sessionStorageService.getCustomer();
        const generationDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const fileName = 'BalanceSheetReport_' + new Date().toISOString().slice(0, 10);

        // Helper to safely format currency
        const formatCurrency = (value: number) => {
            return this.currencyPipe.transform(value, this.reportCurrencyCode, 'symbol', '1.2-2') || '';
        };

        // 2. Define the CSS for a clean print layout
        const styles = `
        <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"; font-size: 12px; }
            .report-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #666; padding-bottom: 10px; margin-bottom: 20px; }
            .report-header .logo { max-height: 50px; }
            .report-header .company-info { text-align: left; }
            .report-header .user-info { display: flex; align-items: center; text-align: right; }
            .report-header .user-info img { width: 40px; height: 40px; border-radius: 50%; margin-left: 10px; }
            .report-title { text-align: center; margin-bottom: 20px; }
            .summary-table, .data-table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
            .summary-table td { padding: 8px; border: 1px solid #ddd; }
            .data-table th, .data-table td { border: 1px solid #ccc; padding: 6px; text-align: left; }
            .data-table th { background-color: #f2f2f2; font-weight: bold; }
            .data-table .section-title { font-size: 1.2em; font-weight: bold; background-color: #e0e0e0; }
            .data-table .total-row { font-weight: bold; background-color: #f2f2f2; }
            .text-right { text-align: right !important; }
            .font-bold { font-weight: bold; }
            @media print {
                body { -webkit-print-color-adjust: exact; }
            }
        </style>
    `;

        // 3. Build the HTML structure for the report
        let reportHtml = `
        <html>
            <head>
                <title>${fileName}</title>
                ${styles}
            </head>
            <body>
                <div class="report-header">
                    <div class="company-info">
                        <img src="${company?.logo || 'https://placehold.co/150x50'}" alt="Company Logo" class="logo">
                        <h1>${company?.name || 'Your Company'}</h1>
                    </div>
                    <div class="user-info">
                        <div>
                            <strong>Printed By:</strong> ${user?.name || 'N/A'}<br>
                            <strong>Date:</strong> ${generationDate}
                        </div>
                        <img src="${user?.imageUrl || 'https://placehold.co/40x40'}" alt="User Avatar">
                    </div>
                </div>

                <div class="report-title"><h1>Balance Sheet Report</h1></div>

                <table class="summary-table">
                    <tr><td>Total Assets</td><td class="text-right">${formatCurrency(this.totalAssets)}</td></tr>
                    <tr><td>Total Liabilities</td><td class="text-right">${formatCurrency(this.totalLiabilities)}</td></tr>
                    <tr><td>Total Equity</td><td class="text-right">${formatCurrency(this.totalEquity)}</td></tr>
                    <tr><td class="font-bold">Assets - (Liabilities + Equity)</td><td class="text-right font-bold" style="color: ${this.totalAssets - (this.totalLiabilities + this.totalEquity) === 0 ? 'green' : 'red'};">${formatCurrency(this.totalAssets - (this.totalLiabilities + this.totalEquity))}</td></tr>
                </table>
    `;

        // Reusable helper function to build an HTML table for a section
        const buildSectionHtml = (title: string, items: BalanceSheetItem[], categoryType: 'asset' | 'liability' | 'equity'): string => {
            if (items.length === 0) return '';

            let tableHtml = `<table class="data-table">`;
            // Section Header
            tableHtml += `<thead><tr><th colspan="${2 + this.selectedPeriods.length}" class="section-title">${title}</th></tr>`;
            // Column Headers
            tableHtml += `<tr><th>Account Code</th><th>Account Name</th>`;
            this.selectedPeriods.forEach(p => tableHtml += `<th class="text-right">${p}</th>`);
            tableHtml += `</tr></thead><tbody>`;

            // Data Rows (recursive)
            const buildRows = (nodes: BalanceSheetItem[]) => {
                nodes.forEach(node => {
                    tableHtml += `<tr class="${node.isSummaryRow ? 'font-bold' : ''}">
                            <td style="padding-left: ${node.level * 15 + 6}px;">${node.accountCode}</td>
                            <td>${node.accountName}</td>`;
                    this.selectedPeriods.forEach(p => tableHtml += `<td class="text-right">${formatCurrency(node.balances[p] || 0)}</td>`);
                    tableHtml += `</tr>`;
                    if (node.children) buildRows(node.children);
                });
            };
            // Only pass top-level items for flattening in print
            const topLevelSectionItems = this.hierarchicalBalanceSheetItems.filter(item => item.internalType === categoryType);
            buildRows(topLevelSectionItems);

            // Footer Total Row
            tableHtml += `</tbody><tfoot><tr class="total-row">
                    <td colspan="2">Total ${title}</td>`;
            this.selectedPeriods.forEach(p => tableHtml += `<td class="text-right">${formatCurrency(this.getTotalForCategory(categoryType, p))}</td>`);
            tableHtml += `</tr></tfoot></table>`;
            return tableHtml;
        };

        // Add Assets, Liabilities, and Equity sections to the report
        reportHtml += buildSectionHtml('Assets', this.assetItems, 'asset');
        reportHtml += buildSectionHtml('Liabilities', this.liabilityItems, 'liability');
        reportHtml += buildSectionHtml('Equity', this.equityItems, 'equity');

        // Add Balance Sheet Equation Check table to the report
        if (this.balanceCheckItems.length > 0) {
            reportHtml += `<h2 class="report-title">Balance Sheet Equation Check</h2>`;
            reportHtml += `<table class="data-table">
            <thead>
                <tr>
                    <th>Metric</th>`;
            this.selectedPeriods.forEach(p => reportHtml += `<th class="text-right">${p}</th>`);
            reportHtml += `</tr>
            </thead>
            <tbody>`;
            this.balanceCheckItems.forEach(item => {
                reportHtml += `<tr><td>${item.metric}</td>`;
                this.selectedPeriods.forEach(period => {
                    const value = item.values[period] || 0;
                    const color = value === 0 ? 'green' : 'red';
                    reportHtml += `<td class="text-right" style="color: ${color};">${formatCurrency(value)}</td>`;
                });
                reportHtml += `</tr>`;
            });
            reportHtml += `</tbody></table>`;
        }

        reportHtml += `</body></html>`;

        // 4. Open a new window and print the generated HTML
        const printWindow = window.open('', '_blank', 'height=800,width=1000');
        if (printWindow) {
            printWindow.document.write(reportHtml);
            printWindow.document.close();
            printWindow.focus(); // Necessary for some browsers
            setTimeout(() => { // Timeout to ensure content is rendered
                printWindow.print();
                printWindow.close();
            }, 500);
        }

        this.messageService.add({ severity: 'info', summary: 'Print', detail: 'Print dialog initiated.' });
    }
}
